'use client';
import { trackedEntity } from '@cineamo/pulse';
import { usePulseTracker } from '@cineamo/pulse/react';
import { useTranslations } from 'next-intl';
import type React from 'react';
import { useState } from 'react';

import { ShowingDto } from '@cineamo/api/models/showing/ShowingDto.types';

import { PurchaseReasonEnum } from '@cineamo/api/constants/PurchaseReason.enum';

import { onTicketButtonClick } from '@/helpers/ticket-purchase-intent-helper';
import { isDatetimePast } from '@/helpers/time-helper';
import { cn } from '@cineamo/ui/src/helpers/cn';

import { useDomainConfiguration } from '@/providers/DomainConfigurationProvider';

import { NoOnlineTicketUrlModal } from '../NoOnlineTicketUrlModal';
import { useTicketUrl } from '../ticket-shape/TicketLink';
import TicketShape from '../ticket-shape/TicketShape';

const BuyTicketButton: React.FC<{
    onBuy: (ticketUrl: string) => void;
    showing: ShowingDto;
    movieId?: number;
    className?: string;
    /**
     * Renders the CTA as a real, crawlable `<a href>` instead of a client-only button, and gates on
     * past showings with a disabled state — used on the canonical showing detail page so the ticket
     * link is followable without JS (SEO). The `ShowingInfoModal` usage keeps the original button,
     * whose `onBuy` callback (not a URL) drives navigation there, so it's left untouched by default.
     */
    renderAsLink?: boolean;
}> = ({ onBuy, showing, movieId, className, renderAsLink }) => {
    const domainConfiguration = useDomainConfiguration();
    const t = useTranslations();
    const { track: pulseTrack } = usePulseTracker();

    const [noTicketUrlIsOpen, setNoTicketUrlIsOpen] = useState(false);
    const { ticketUrl, hasTicketUrl, isExternal, target, isHandoff } = useTicketUrl(showing, movieId);
    const isPast = renderAsLink && isDatetimePast(showing.startDatetime, true);

    function trackTicketClick() {
        onTicketButtonClick(showing.id, PurchaseReasonEnum.CINEMA_WEBSITE, showing.contentId);
        if (!isHandoff) {
            pulseTrack({
                ...trackedEntity({ movieId, contentId: showing.contentId }),
                event_type: 'ticket_click',
                context: { showing_id: showing.id }
            });
        }
    }

    const ticketShape = (
        <TicketShape
            style={{ borderTopRightRadius: '0', borderBottomRightRadius: '0' }}
            config={{
                fillColorHex: domainConfiguration.style.primaryColor,
                fillColorOpacity: isPast ? 0.4 : 1
            }}>
            <span
                className={cn(
                    'leading-[24px] px-12 py-6 relative text-body-bold-16 block',
                    isPast && 'opacity-60',
                    'text-[var(--on-primary-color)]'
                )}>
                {t('showing-info.cta')}
            </span>
        </TicketShape>
    );

    return (
        <>
            {noTicketUrlIsOpen && (
                <NoOnlineTicketUrlModal
                    modalIsOpen={noTicketUrlIsOpen}
                    setModalIsOpen={setNoTicketUrlIsOpen}
                />
            )}

            {isPast ? (
                <div
                    className={cn('w-full mt-16 cursor-not-allowed', className)}
                    aria-disabled="true">
                    {ticketShape}
                </div>
            ) : renderAsLink && hasTicketUrl ? (
                <a
                    className={cn('w-full mt-16 block', className)}
                    href={ticketUrl}
                    target={target}
                    rel={isExternal ? 'nofollow noopener noreferrer' : undefined}
                    onClick={() => {
                        trackTicketClick();
                        if (ticketUrl) onBuy(ticketUrl);
                    }}>
                    {ticketShape}
                    {isExternal && <span className="sr-only">{t('program.ticket-external-hint')}</span>}
                </a>
            ) : (
                <button
                    className={cn('w-full mt-16', className)}
                    onClick={() => {
                        if (!hasTicketUrl) {
                            setNoTicketUrlIsOpen(true);
                        } else {
                            trackTicketClick();
                            if (ticketUrl) onBuy(ticketUrl);
                        }
                    }}>
                    {ticketShape}
                </button>
            )}
        </>
    );
};

export default BuyTicketButton;
